import React, { createContext, useContext, useEffect, useState } from "react";
import { onAuthStateChanged, User as FirebaseUser } from "firebase/auth";
import { doc, getDoc, setDoc, Timestamp } from "firebase/firestore";
import { auth, db } from "@/lib/firebase";
import { trackActivity } from "@/lib/activity";

interface AuthContextType {
  user: FirebaseUser | null;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
});

function ensureUserDoc(user: FirebaseUser): Promise<boolean> {
  // Fire-and-forget — never blocks auth
  // Returns true if the user doc was newly created
  return getDoc(doc(db, "users", user.uid))
    .then((snap) => {
      if (!snap.exists()) {
        return setDoc(doc(db, "users", user.uid), {
          email: user.email,
          displayName: user.displayName || user.email?.split("@")[0] || "",
          createdAt: Timestamp.now(),
        }).then(() => true);
      }
      return false;
    })
    .catch((err) => {
      console.error("[auth-ctx] ensureUserDoc error:", err?.message);
      return false;
    });
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser) {
        ensureUserDoc(firebaseUser).then((isNew) => {
          trackActivity(isNew ? "sign_up" : "login", firebaseUser.uid, firebaseUser.email);
        });
        // Register push token (no-op on web/simulator)
        import("@/lib/pushNotifications").then((m) => m.registerPushToken(firebaseUser.uid)).catch(() => {});
      }
      setUser(firebaseUser);
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
